
import React, { useEffect, useState, useCallback, useRef } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import PartySelectSection from '../../../../documents/components/PartySelectSection'
import { request } from '../../../../../../utils/request';
import { useForm } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  Typography,
  Alert,
  Divider,
  Button,
  LinearProgress,
  Chip
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import InvoiceDatesSection from '../../../../documents/components/InvoiceDatesSection';
import NotesTermsSection from '../../../../documents/components/NotesTermsSection';
import AmountSummarySection from '../../../../documents/components/AmountSummarySection';
import { GstType, InvoiceOption, PaymentInFormData } from '../../../../types';
import { useGstMode } from '../../../../../../utils/useGstMode';
import { toast } from 'react-toastify';
import { useFinancialYear } from '../../../../../../utils/useFinancialYear';
import { useSearchParams } from 'react-router-dom';
import InvoiceSelector from '../../../../documents/components/InvoiceSelector';

interface PaymentDistribution {
  invoice: InvoiceOption;
  amount: number;
  type: 'FULL' | 'PARTIAL';
}

interface PaymentResult {
  invoiceNumber: string;
  amount: number;
  success: boolean;
  paymentNumber?: string;
  error?: string;
}

const CreatePaymentIn = () => {
  const [searchParams] = useSearchParams();
  const financialYear = useFinancialYear();
  const defaultPaymentInNumber = `PI${financialYear}-1`
  const navigate = useNavigate();
  const { gstEnabled } = useGstMode();
  const { id } = useParams();
  const isEditMode = !!id;

  const [nextPaymentInNumber, setNextPaymentInNumber] = useState(defaultPaymentInNumber);
  const [isLoadingPaymentInNumber, setIsLoadingPaymentInNumber] = useState(false);
  const [loading, setLoading] = useState(false);

  const [selectedInvoices, setSelectedInvoices] = useState<InvoiceOption[]>([]);
  const [, setTotalSelectedAmount] = useState(0);
  const [totalDueAmount, setTotalDueAmount] = useState(0);
  const [availableInvoices, setAvailableInvoices] = useState<InvoiceOption[]>([]);

  // Multi-payment submission state
  const [isSubmittingMultiple, setIsSubmittingMultiple] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState({ current: 0, total: 0 });
  const [paymentResults, setPaymentResults] = useState<PaymentResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Auto-select timeout ref
  const autoSelectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const queryGstType = searchParams.get('gstType');

  // Determine default GST type based on query parameter or gstEnabled
  const getDefaultGstType = (): GstType => {
    if (queryGstType === 'NON-GST') {
      return 'NON-GST';
    }
    return gstEnabled ? 'GST' : 'GST';
  };

  const methods = useForm<PaymentInFormData>({
    defaultValues: {
      paymentInType: 'PAYMENT_IN',
      paymentType: 'CASH',
      gstType: getDefaultGstType(),
      party: '',
      invoiceId: "",
      paymentInNumber: '',
      paymentInDate: new Date().toISOString().split('T')[0],
      date: new Date().toISOString().split('T')[0],
      discount: undefined,
      notes: '',
      receivedAmount: 0,
    },
    mode: 'onChange'
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    getValues,
    reset
  } = methods;

  // Watch values
  const gstType = watch('gstType', getDefaultGstType());
  const paymentInType = watch('paymentInType', 'PAYMENT_IN');
  const selectedPartyId = watch('party');
  const receivedAmount = watch('receivedAmount') || 0;

  // Fetch unpaid invoices when party changes
  useEffect(() => {
    const fetchUnpaidInvoices = async () => {
      if (!selectedPartyId) {
        setAvailableInvoices([]);
        setTotalDueAmount(0);
        setSelectedInvoices([]); // Clear selection when party changes
        setTotalSelectedAmount(0);
        return;
      }

      try {
        const res = await request({
          url: 'invoice/get-all-sales-invoices',
          method: 'get',
          params: {
            partyId: selectedPartyId,
            gstType: gstType,
            limit: 100
          }
        });

        if (res.success && res.data) {
          const unpaidInvoices = res.data.filter((inv: InvoiceOption) =>
            (inv.balanceAmount || 0) > 0
          );

          setAvailableInvoices(unpaidInvoices);

          const total = unpaidInvoices.reduce((sum: number, inv: InvoiceOption) =>
            sum + (inv.balanceAmount || 0), 0
          );
          setTotalDueAmount(total);
        }
      } catch (error) {
        console.error('Error fetching unpaid invoices:', error);
      }
    };

    fetchUnpaidInvoices();
  }, [selectedPartyId, gstType]);

  // Handle invoice selection (called from InvoiceSelector when user manually selects)
  const handleInvoiceSelection = useCallback((invoices: InvoiceOption[], totalAmount: number) => {
    setSelectedInvoices(invoices);
    setTotalSelectedAmount(totalAmount);

    // Clear previous results when selection changes
    setPaymentResults([]);
    setShowResults(false);
  }, []);

  // Auto-select invoices based on amount
  useEffect(() => {
    // Don't auto-select if no party, no amount, or no invoices
    if (!selectedPartyId || !receivedAmount || receivedAmount <= 0 || availableInvoices.length === 0) {
      return;
    }

    // Clear previous timeout
    if (autoSelectTimeoutRef.current) {
      clearTimeout(autoSelectTimeoutRef.current);
    }

    // Debounce auto-select
    autoSelectTimeoutRef.current = setTimeout(() => {

      // Sort invoices by date (oldest first)
      const sortedInvoices = [...availableInvoices].sort((a, b) =>
        new Date(a.invoiceDate).getTime() - new Date(b.invoiceDate).getTime()
      );

      let remainingAmount = receivedAmount;
      const selectedInvoicesList: InvoiceOption[] = [];

      // Select invoices until amount is exhausted
      for (const invoice of sortedInvoices) {
        if (remainingAmount <= 0) break;

        const balance = invoice.balanceAmount || 0;
        if (balance <= 0) continue;

        // Always select the invoice, even for partial payment
        selectedInvoicesList.push(invoice);

        if (remainingAmount >= balance) {
          remainingAmount -= balance;
        } else {
          remainingAmount = 0; // Partial payment, stop after this invoice
        }
      }

      // Update selection if changed
      if (selectedInvoicesList.length > 0) {
        const totalAmount = selectedInvoicesList.reduce(
          (sum, inv) => sum + (inv.balanceAmount || 0), 0
        );

        // Update state - this will trigger re-render and pass selectedInvoiceIds to InvoiceSelector
        setSelectedInvoices(selectedInvoicesList);
        setTotalSelectedAmount(totalAmount);
      } else {
        // Clear selection if no invoices match
        setSelectedInvoices([]);
        setTotalSelectedAmount(0);
      }

    }, 500);

    return () => {
      if (autoSelectTimeoutRef.current) {
        clearTimeout(autoSelectTimeoutRef.current);
      }
    };
  }, [receivedAmount, selectedPartyId, availableInvoices]);



  // Calculate payment distribution (FIFO - oldest first)
  const calculatePaymentDistribution = useCallback((): PaymentDistribution[] => {
    if (!receivedAmount || selectedInvoices.length === 0) return [];

    let remainingAmount = receivedAmount;
    const distribution: PaymentDistribution[] = [];

    // Sort invoices by date (oldest first) for FIFO payment
    const sortedInvoices = [...selectedInvoices].sort((a, b) =>
      new Date(a.invoiceDate).getTime() - new Date(b.invoiceDate).getTime()
    );

    for (const invoice of sortedInvoices) {
      if (remainingAmount <= 0) break;

      const balance = invoice.balanceAmount || 0;

      if (remainingAmount >= balance) {
        // Full payment
        distribution.push({
          invoice,
          amount: balance,
          type: 'FULL'
        });
        remainingAmount -= balance;
      } else {
        // Partial payment
        distribution.push({
          invoice,
          amount: remainingAmount,
          type: 'PARTIAL'
        });
        remainingAmount = 0;
      }
    }

    return distribution;
  }, [selectedInvoices, receivedAmount]);

  const paymentDistribution = calculatePaymentDistribution();
  const totalDistributed = paymentDistribution.reduce((sum, d) => sum + d.amount, 0);
  const remainingAmount = (receivedAmount || 0) - totalDistributed;

  // Fetch next payment number for each invoice
  const fetchNextPaymentNumber = useCallback(async (index: number): Promise<string> => {
    try {
      const res = await request({
        url: 'paymentIn/get-next-payment-in-number',
        method: "get",
        params: {
          paymentInType: paymentInType,
          gstType: gstType
        }
      });

      if (res.success && res.paymentInNumber) {
        return res.paymentInNumber;
      }
    } catch (error) {
      console.error(`Error fetching payment number for invoice ${index + 1}:`, error);
    }

    // Fallback
    return `${defaultPaymentInNumber}-${Date.now()}-${index + 1}`;
  }, [gstType, paymentInType, defaultPaymentInNumber]);

  // Submit single payment
  const submitSinglePayment = async (
    data: PaymentInFormData,
    distribution: PaymentDistribution,
    paymentNumber: string
  ): Promise<PaymentResult> => {
    try {
      const payload = {
        ...data,
        party: data.party,
        invoiceId: distribution.invoice._id,
        receivedAmount: distribution.amount,
        paymentInNumber: paymentNumber,
        notes: `${data.notes || ''} ${distribution.type === 'PARTIAL' ? '(Partial payment)' : ''}`.trim(),
        paymentGroup: `GROUP-${Date.now()}`,
        isBulkPayment: true
      };

      const res = await request({
        url: 'paymentIn/create-payment-in',
        method: 'post',
        data: payload
      });

      if (res.success) {
        return {
          invoiceNumber: distribution.invoice.invoiceNumber,
          amount: distribution.amount,
          success: true,
          paymentNumber: paymentNumber
        };
      } else {
        return {
          invoiceNumber: distribution.invoice.invoiceNumber,
          amount: distribution.amount,
          success: false,
          error: res.message || 'Payment failed'
        };
      }
    } catch (error: any) {
      return {
        invoiceNumber: distribution.invoice.invoiceNumber,
        amount: distribution.amount,
        success: false,
        error: error.message || 'Payment failed'
      };
    }
  };

  // Handle form submission for multiple payments
  const onSubmitMultiplePayments = async (data: PaymentInFormData) => {
    if (selectedInvoices.length === 0) {
      toast.error('Please select at least one invoice');
      return;
    }

    if (receivedAmount <= 0) {
      toast.error('Please enter payment amount');
      return;
    }

    if (paymentDistribution.length === 0) {
      toast.error('No invoices can be paid with this amount');
      return;
    }

    setIsSubmittingMultiple(true);
    setSubmissionProgress({ current: 0, total: paymentDistribution.length });
    setPaymentResults([]);
    setShowResults(false);

    const results: PaymentResult[] = [];

    // Process payments sequentially
    for (let i = 0; i < paymentDistribution.length; i++) {
      const distribution = paymentDistribution[i];
      setSubmissionProgress({ current: i + 1, total: paymentDistribution.length });

      try {
        const paymentNumber = await fetchNextPaymentNumber(i);
        const result = await submitSinglePayment(data, distribution, paymentNumber);
        results.push(result);

        if (result.success) {
          toast.success(`Payment ${i + 1}/${paymentDistribution.length}: Invoice ${result.invoiceNumber} - ₹${result.amount.toFixed(2)}`, {
            autoClose: 2000
          });
        } else {
          toast.error(`Payment ${i + 1} failed: ${result.error}`, {
            autoClose: 3000
          });
        }

      } catch (error: any) {
        results.push({
          invoiceNumber: distribution.invoice.invoiceNumber,
          amount: distribution.amount,
          success: false,
          error: error.message || 'Payment failed'
        });
      }

      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setPaymentResults(results);
    setShowResults(true);
    setIsSubmittingMultiple(false);

    const successful = results.filter(r => r.success).length;
    const failed = results.filter(r => !r.success).length;

    if (failed === 0) {
      toast.success(`✅ All ${successful} payments completed successfully!`);
      setTimeout(() => navigate('/accounts/sales/paymentIn'), 2000);
    } else if (successful > 0) {
      toast.warning(`⚠️ ${successful} payments succeeded, ${failed} failed. Check results below.`);
    } else {
      toast.error(`❌ All ${failed} payments failed. Please try again.`);
    }
  };

  // Handle single invoice submission
  const onSubmitSingleInvoice = async (data: PaymentInFormData) => {
    try {
      const payload = {
        ...data,
        party: data.party,
        invoiceId: selectedInvoices[0]._id,
        receivedAmount: data.receivedAmount,
        paymentInNumber: isEditMode ? data.paymentInNumber : (data.paymentInNumber || undefined),
      };

      const apiUrl = isEditMode
        ? `paymentIn/update-payment-in/${id}`
        : 'paymentIn/create-payment-in';
      const method = isEditMode ? 'put' : 'post';

      const res = await request({
        url: apiUrl,
        method: method,
        data: payload
      });

      if (res.success) {
        toast.success(res.message || "Payment completed successfully");
        navigate('/accounts/sales/paymentIn');
      } else {
        toast.error(res.message || "Operation failed");
      }
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong');
    }
  };

  // Main onSubmit handler
  const onSubmit = async (data: PaymentInFormData) => {
    if (selectedInvoices.length === 1) {
      await onSubmitSingleInvoice(data);
    } else {
      await onSubmitMultiplePayments(data);
    }
  };

  // Fetch next payment in number
  const fetchNextPaymentInNumber = useCallback(async () => {
    if (isEditMode) return;

    setIsLoadingPaymentInNumber(true);
    try {
      const res = await request({
        url: 'paymentIn/get-next-payment-in-number',
        method: "get",
        params: { paymentInType, gstType }
      });

      if (res.success && res.paymentInNumber) {
        setNextPaymentInNumber(res.paymentInNumber);
        setValue('paymentInNumber', res.paymentInNumber);
      }
    } catch (error) {
      setNextPaymentInNumber(defaultPaymentInNumber);
      setValue('paymentInNumber', defaultPaymentInNumber);
    } finally {
      setIsLoadingPaymentInNumber(false);
    }
  }, [gstType, paymentInType, defaultPaymentInNumber, isEditMode, setValue]);

  // Fetch existing payment in data for edit mode
  const fetchPaymentInData = useCallback(async () => {
    if (!isEditMode) return;

    setLoading(true);
    try {
      const res = await request({
        url: `paymentIn/get-single-payment-in/${id}`,
        method: "get"
      });

      if (res.success && res.data) {
        const paymentInData = res.data;

        const formatDate = (dateString: string | Date) => {
          if (!dateString) return new Date().toISOString().split('T')[0];
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        };

        let partyId = '';
        if (typeof paymentInData.party === 'object' && paymentInData.party !== null) {
          partyId = paymentInData.party._id || '';
        } else if (typeof paymentInData.party === 'string') {
          partyId = paymentInData.party;
        }

        const formData = {
          paymentInType: paymentInData.paymentInType || 'PAYMENT_IN',
          gstType: paymentInData.gstType || 'GST',
          paymentType: paymentInData.paymentType,
          invoiceId: paymentInData.invoiceId?._id || undefined,
          party: partyId,
          paymentInNumber: paymentInData.paymentInNumber || '',
          paymentInDate: formatDate(paymentInData.paymentInDate),
          date: formatDate(paymentInData.date),
          discount: paymentInData.discount || undefined,
          notes: paymentInData.notes || '',
          receivedAmount: paymentInData.receivedAmount || 0,
        };

        reset(formData);
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to load payment in data");
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode, reset]);

  // Initial data fetch
  useEffect(() => {
    if (isEditMode) {
      fetchPaymentInData();
    } else {
      fetchNextPaymentInNumber();
    }
  }, [isEditMode, fetchPaymentInData, fetchNextPaymentInNumber]);

  // Update next payment number when type changes
  useEffect(() => {
    if (!isEditMode) {
      fetchNextPaymentInNumber();
    }
  }, [paymentInType, isEditMode, fetchNextPaymentInNumber]);

  const requestWrapper = ({ url, method, params }: { url: string; method: string; params?: any }) => {
    return request({ method: method as any, url, params });
  }

  const handleGstTypeChange = (event: React.MouseEvent<HTMLElement>, newGstType: GstType | null) => {
    if (newGstType !== null) {
      setValue('gstType', newGstType);
    }
  };

  const handleCancel = () => {
    navigate('/accounts/sales/paymentIn');
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography>Loading Payment In data...</Typography>
      </Box>
    );
  }

  return (
    <>
      <DocumentHeader
        heading={isEditMode ? 'Update Payment In' : 'Record Payment In'}
        showBackButton
        showCancelButton={true}
        showSaveButton={false}
        onCancel={handleCancel}
        saveDisabled={isSubmitting || isSubmittingMultiple}
      />

      {!gstEnabled && (
        <Card sx={{ mb: 2, p: 2, borderRadius: 1, boxShadow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" fontWeight="medium">
              Payment In Type:
            </Typography>
            <ToggleButtonGroup
              value={gstType}
              exclusive
              onChange={handleGstTypeChange}
              size="small"
              color="primary"
              disabled={isEditMode}
            >
              <ToggleButton value="GST">GST Payment In</ToggleButton>
              <ToggleButton value="NON-GST">Non-GST Payment In</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Card>
      )}

      <FormProvider {...methods}>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Party and Invoice Number Section */}
          <Box display="flex" gap={2}>
            <Box flex={1}>
              <PartySelectSection
                request={requestWrapper}
                apiParams={{ status: 'active', limit: 50 }}
                billToLabel="Party"
                formName="party"
                onAddNewParty={() => navigate('/parties/addParty')}
                compact={true}
                sendPartyIdOnly={true}
                showTotalDue={true}
                totalDue={totalDueAmount}
              />
              {errors.party && (
                <Typography color="error" variant="caption">
                  Please select a party
                </Typography>
              )}
            </Box>
            <Box flex={1}>
              <InvoiceDatesSection
                invoiceNumberLabel="Payment In No"
                invoiceDateLabel="Payment In Date"
                formFieldNames={{
                  invoiceNumber: 'paymentInNumber',
                  invoiceDate: 'paymentInDate',
                  invoiceId: 'invoiceId'
                }}
                partyId={selectedPartyId}
                gstType={gstType}
                autoGenerateInvoiceNumber={!isEditMode}
                nextInvoiceNumber={nextPaymentInNumber}
                showPaymentTerms={false}
                showDueDate={false}
                isInvoiceLink={false}
                compact={true}
                disabled={isLoadingPaymentInNumber || isEditMode}
                onInvoiceDateChange={() => { }}
              />
            </Box>
          </Box>

          {/* Notes and Amount Section */}
          <Box display="flex" gap={2}>
            <Box flex={1}>
              <NotesTermsSection
                notesLabel="Additional Notes"
                formFieldNames={{ notes: 'notes' }}
                showNotes={true}
                showTerms={false}
                compact={true}
                maxNotesLength={500}
                onNotesChange={() => { }}
                defaultExpanded={!!getValues('notes')}
              />
            </Box>
            <Box flex={1}>
              <AmountSummarySection
                showTaxableAmount={false}
                showCharges={false}
                showRoundOff={false}
                showBalanceAmount={false}
                showFullyPaid={false}
                showSubTotal={false}
                showTotalAmount={false}
                showDivider={false}
                formFieldNames={{
                  discount: 'discount',
                  paymentType: 'paymentType',
                  receivedAmount: 'receivedAmount',
                }}
                compact={true}
                taxRate={gstType === 'GST' ? 18 : 0}
                onTotalAmountChange={() => { }}
              />
            </Box>
          </Box>

          {/* Invoice Selector */}
          <Box sx={{ mt: 2 }}>
            <InvoiceSelector
              partyId={selectedPartyId}
              gstType={gstType}
              onSelectionChange={handleInvoiceSelection}
              selectedInvoiceIds={selectedInvoices.map(inv => inv._id)}
              multiple={true}
              showBalanceOnly={true}
              compact={true}
              title="Select Invoices to Settle"
            />
          </Box>

          {/* Auto-select Hint */}
          {selectedPartyId && receivedAmount > 0 && selectedInvoices.length === 0 && availableInvoices.length > 0 && (
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              No invoices selected. Invoices will be auto-selected as you type the amount.
            </Alert>
          )}

          {/* Payment Distribution Preview */}
          {selectedInvoices.length > 0 && receivedAmount > 0 && paymentDistribution.length > 0 && (
            <Card sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" gutterBottom>
                Payment Distribution Preview ({paymentDistribution.length} payments)
              </Typography>

              {paymentDistribution.map((dist, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                    p: 1,
                    bgcolor: dist.type === 'PARTIAL' ? 'warning.50' : 'success.50',
                    borderRadius: 1
                  }}
                >
                  <Box>
                    <Typography variant="body2">
                      {dist.invoice.invoiceNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Balance: ₹{dist.invoice.balanceAmount?.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight={600}>
                      ₹{dist.amount.toFixed(2)}
                    </Typography>
                    <Chip
                      label={dist.type}
                      size="small"
                      color={dist.type === 'PARTIAL' ? 'warning' : 'success'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Box>
              ))}

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Total Payment</Typography>
                <Typography variant="subtitle2" color="primary.main">
                  ₹{receivedAmount.toFixed(2)}
                </Typography>
              </Box>

              {remainingAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Unallocated Amount
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    ₹{remainingAmount.toFixed(2)}
                  </Typography>
                </Box>
              )}

              {selectedInvoices.length > 1 && (
                <Alert severity="info" sx={{ mt: 2, fontSize: '0.875rem' }}>
                  <strong>{paymentDistribution.length}</strong> separate payments will be created
                </Alert>
              )}
            </Card>
          )}

          {/* Submission Progress */}
          {isSubmittingMultiple && (
            <Card sx={{ p: 2 }}>
              <Typography variant="body2" gutterBottom>
                Processing payments... {submissionProgress.current}/{submissionProgress.total}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(submissionProgress.current / submissionProgress.total) * 100}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Card>
          )}

          {/* Payment Results */}
          {showResults && paymentResults.length > 0 && (
            <Card sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Payment Results
              </Typography>

              {paymentResults.map((result, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                    p: 1,
                    bgcolor: result.success ? 'success.50' : 'error.50',
                    borderRadius: 1
                  }}
                >
                  <Box>
                    <Typography variant="body2">
                      {result.invoiceNumber}
                    </Typography>
                    {result.paymentNumber && (
                      <Typography variant="caption" color="text.secondary">
                        Ref: {result.paymentNumber}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight={600}>
                      ₹{result.amount.toFixed(2)}
                    </Typography>
                    <Chip
                      label={result.success ? 'Success' : 'Failed'}
                      size="small"
                      color={result.success ? 'success' : 'error'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Box>
              ))}

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">
                  Successful: {paymentResults.filter(r => r.success).length}
                </Typography>
                <Typography variant="body2" color="success.main">
                  Failed: {paymentResults.filter(r => !r.success).length}
                </Typography>
              </Box>
            </Card>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            size="large"
            onClick={handleSubmit(onSubmit)}
            disabled={
              isSubmitting ||
              isSubmittingMultiple ||
              selectedInvoices.length === 0 ||
              !receivedAmount ||
              receivedAmount <= 0
            }
            sx={{ mt: 2 }}
          >
            {isSubmittingMultiple
              ? `Processing ${submissionProgress.current}/${submissionProgress.total}...`
              : isSubmitting
                ? 'Submitting...'
                : selectedInvoices.length > 1
                  ? `Submit ${paymentDistribution.length} Payments (₹${totalDistributed.toFixed(2)})`
                  : 'Submit Payment'}
          </Button>
        </Box>
      </FormProvider>
    </>
  )
}

export default CreatePaymentIn;